import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { SkillCardProps } from './SkillCard';
import { getSkillThumbnailUrl } from '../../client/SkillClient';
import './SkillDetailModal.css';

interface SkillDetailModalProps {
  skill: SkillCardProps;
  onClose: () => void;
  onToggle?: (skillName: string, agentName: string, equip: boolean) => Promise<void>;
}

export default function SkillDetailModal({ skill, onClose, onToggle }: SkillDetailModalProps) {
  const { t } = useTranslation('skills');
  const [toggling, setToggling] = useState(false);
  const [imgUrl, setImgUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!skill.thumbnailUrl) return;
    let cancelled = false;
    getSkillThumbnailUrl(skill.thumbnailUrl).then((url) => {
      if (!cancelled) setImgUrl(url);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [skill.thumbnailUrl]);

  // Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleToggle = async () => {
    if (!onToggle || !skill.skillName || toggling) return;
    setToggling(true);
    try {
      await onToggle(skill.skillName, skill.agentName || '', !skill.enabled);
    } finally {
      setToggling(false);
      onClose();
    }
  };

  return (
    <div className="skill-modal-overlay" onClick={onClose}>
      <div className="skill-modal" role="dialog" aria-modal="true" aria-labelledby="skill-modal-title" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="skill-modal__header">
          <h2 id="skill-modal-title" className="skill-modal__title">{skill.title}</h2>
          <button className="skill-modal__close" onClick={onClose} aria-label={t('modal.close')}>✕</button>
        </div>

        {/* Description */}
        <div className="skill-modal__description">
          <p>{skill.description || t('modal.noDescription')}</p>
          {skill.tags && skill.tags.length > 0 && (
            <div className="skill-modal__tags">
              {skill.tags.map((tag) => (
                <span key={tag} className="skill-modal__badge">{tag}</span>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="skill-modal__preview">
          {imgUrl ? (
            <img src={imgUrl} alt={skill.title} className="skill-modal__preview-img" onError={() => setImgUrl(null)} />
          ) : (
            <div className="skill-modal__preview-placeholder">{t('modal.noPreview')}</div>
          )}
        </div>

        {/* Footer */}
        <div className="skill-modal__footer">
          <button className="skill-modal__btn skill-modal__btn--secondary" onClick={onClose}>{t('modal.cancel')}</button>
          <button
            className={`skill-modal__btn ${skill.enabled ? 'skill-modal__btn--danger' : 'skill-modal__btn--primary'}`}
            onClick={handleToggle}
            disabled={toggling || !onToggle}
          >
            {toggling ? t('modal.processing') : skill.enabled ? t('modal.unequip') : t('modal.equip')}
          </button>
        </div>
      </div>
    </div>
  );
}
